/**
 * StickyGetAppBar — slim sticky bar pinned to the bottom of the viewport
 * on marketing / landing pages.
 *
 * Behavior:
 * - Hides automatically inside the native Capacitor app
 * - Hides automatically on mobile when the bottom tab nav is already
 *   showing (we don't want to stack two bars)
 * - Hides on live-break room pages (`/live/:dropId`) so it doesn't
 *   compete with the chat composer / countdown / claims UI
 * - Persistently dismissible via localStorage (per-device)
 * - Brand-gradient pill on the right + short copy on the left
 * - Slides up on scroll past 400px so it doesn't fight the hero
 */
import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import GetAppButton from "./GetAppButton";

const DISMISS_KEY = "blk_get_app_bar_dismissed_v1";

function isInsideNativeApp() {
  if (typeof window === "undefined") return false;
  const cap = window.Capacitor;
  if (cap && (cap.isNativePlatform?.() || cap.isNative)) return true;
  return false;
}

const StickyGetAppBar = () => {
  const [dismissed, setDismissed] = useState(false);
  const [showScrolled, setShowScrolled] = useState(false);
  // Track pathname WITHOUT useLocation() because this component is mounted
  // by <AuthProvider /> which sits OUTSIDE the <BrowserRouter> in the tree.
  // useLocation() would throw "useLocation() may be used only in the
  // context of a <Router>". window.history pushState/popstate cover the
  // navigation events we care about.
  const [pathname, setPathname] = useState(() =>
    typeof window !== "undefined" ? window.location.pathname : "/"
  );

  // Routes where the sticky bar must NEVER appear because it competes
  // with critical in-page UI (chat composer, claim buttons, video
  // overlays, etc.). Add new routes here rather than editing the JSX.
  const HIDDEN_ROUTES = ["/live/", "/play", "/admin", "/locker", "/marketplace"];
  const isHiddenRoute = HIDDEN_ROUTES.some((p) => pathname.startsWith(p));

  useEffect(() => {
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") {
        setDismissed(true);
      }
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (dismissed) return;
    const onScroll = () => setShowScrolled(window.scrollY > 400);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [dismissed]);

  // Sync pathname across SPA navigations. React Router fires popstate on
  // back/forward; we also patch pushState/replaceState so navigate() picks
  // up the hidden-route check without a full page reload.
  useEffect(() => {
    const sync = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", sync);
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function (...args) { origPush.apply(this, args); sync(); };
    window.history.replaceState = function (...args) { origReplace.apply(this, args); sync(); };
    return () => {
      window.removeEventListener("popstate", sync);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, []);

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch (e) {
      /* ignore */
    }
  };

  if (dismissed) return null;
  if (isHiddenRoute) return null;
  if (isInsideNativeApp()) return null;
  if (!showScrolled) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[80] max-w-[min(640px,calc(100vw-32px))] w-full px-3 sm:px-4 transition-all"
      data-testid="sticky-get-app-bar"
      style={{
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
      }}
    >
      <div
        className="relative flex items-center gap-3 sm:gap-4 rounded-full bg-black/90 backdrop-blur-md border border-orange-500/30 shadow-[0_20px_60px_-12px_rgba(249,115,22,0.5)] px-3 sm:px-4 py-2"
      >
        {/* Brand mark — uses the same gradient logo as the favicon /
            apple-touch-icon for visual continuity. Previously rendered a
            plain capital "B" on a gradient swatch, which looked off-brand
            next to the real logo elsewhere on the site. */}
        <img
          src="/favicon.png"
          alt="BLK PACKS"
          className="w-8 h-8 rounded-full shrink-0 object-cover"
          data-testid="sticky-get-app-logo"
          loading="lazy"
        />
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold text-xs sm:text-sm leading-tight truncate">
            Get the BLK PACKS App
          </p>
          <p className="text-white/50 text-[10px] sm:text-[11px] leading-tight truncate">
            Drops + AR + Live Breaks, in your pocket
          </p>
        </div>
        <GetAppButton size="sm" testId="sticky-get-app-cta" />
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-white/40 hover:text-white/80 transition-colors p-1 shrink-0"
          data-testid="sticky-get-app-dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

export default StickyGetAppBar;
